<template>
	<div class="contact-form" data-aos="fade-up">
		<h2 class="contact-form__title">{{ title ? title : $t('modules.contact_page_modules.contact_page_form.form_title') }}</h2>
		<v-form ref="formRef" v-model="isValid">
			<InputPrimary
				v-model="formValues.name"
				:label="$t('modules.contact_page_modules.contact_page_form.name_label')"
				required
				:rules="[requiredRule]"></InputPrimary>
			<InputPrimary
				v-model="formValues.email"
				:label="$t('modules.contact_page_modules.contact_page_form.email_label')"
				required
				:rules="[requiredRule, emailRule]"></InputPrimary>
			<div
				class="contact-form__phone"
				:class="{
					'contact-form__phone--error': !isFormPhoneValid && phoneInputTouched,
				}">
				<GlobalPhoneInput
					v-model="formValues.phoneNumber"
					:label="$t('components.modal.login.phone')"
					required
					:rules="[requiredRule]"
					validate-on="blur"
					@click="handlePhoneBlur"
					@update="(v) => (isFormPhoneValid = v)" />
			</div>
			<TextareaPrimary
				v-model="formValues.message"
				:label="$t('modules.contact_page_modules.contact_page_form.message_label')"
				required
				:rules="[requiredRule]"
				:rows="3"></TextareaPrimary>
			<div class="contact-form__checkboxes">
				<GlobalCheckbox v-model="formValues.consentToProcessData" required :rules="[requiredRule]">
					<p>
						{{ $t('modules.long_term_page_module.long_term_page_contact.form.consent_to_process_data') }}
						<NuxtLink :to="getLocaleLang() === 'pl' ? '/docs/PP-PL-05.25.pdf' : '/docs/PP-EN-05.25.pdf'" rel="nofollow" target="_blank">{{
							$t('modules.long_term_page_module.long_term_page_contact.form.privacy_policy')
						}}</NuxtLink
						><Asterix />
					</p>
				</GlobalCheckbox>
				<GlobalCheckbox v-model="formValues.marketingConsent">
					<p>{{ $t('modules.long_term_page_module.long_term_page_contact.form.marketing_consent') }}</p>
				</GlobalCheckbox>
			</div>
			<Transition name="fade" mode="out-in">
				<div v-if="!isFormValid" class="contact-form__fill-all-fields">{{ $t('common.fill_all_fields_star') }}(*)</div>
			</Transition>
			<ButtonPrimary class="contact-form__btn" :disabled="!isFormValid || loader" :loading="loader" @click="submit">{{
				$t('modules.contact_page_modules.contact_page_form.submit_button_label')
			}}</ButtonPrimary>
		</v-form>
	</div>
</template>

<script setup lang="ts">
const notyf = useNotyfService()
const $t = useI18n().t
const { requiredRule, emailRule } = useFormRulesService()

const formRef = ref()
const isValid = ref(false)
const loader = ref(false)
const phoneInputTouched = ref(false)
const isFormPhoneValid = ref(true)
const { getLocaleLang } = usei18nService()

const formValues = ref({
	name: '',
	email: '',
	phoneNumber: undefined,
	message: '',
	consentToProcessData: false,
	marketingConsent: false,
})

defineProps<{
	title?: string
}>()

const handlePhoneBlur = () => {
	phoneInputTouched.value = true
}

const isFormValid = computed(() => {
	return isValid.value && isFormPhoneValid.value
})

const submit = async () => {
	if (!isFormValid.value) return
	loader.value = true

	try {
		const obj: IContactFormRequestModel = {
			clientName: formValues.value.name,
			clientEmail: formValues.value.email,
			clientPhoneNumber: formValues.value.phoneNumber ?? '',
			message: formValues.value.message,
			marketingConsent: formValues.value.marketingConsent,
		}

		await ApiService.sendContactForm(obj)
		notyf?.success($t('success.inquiry_sent_to_carfree'))

		formRef.value.reset()
		formRef.value.resetValidation()
		formValues.value.consentToProcessData = false
		formValues.value.marketingConsent = false
		formValues.value.phoneNumber = undefined
		phoneInputTouched.value = false
		isFormPhoneValid.value = true
		formValues.value.name = ''
		formValues.value.email = ''
	} catch (error) {
		console.error(error)
	} finally {
		loader.value = false
	}
}
</script>

<style scoped lang="scss">
@import './Form.scss';
@include fade-animation;
</style>
